import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNodeData } from "../../models/CanvasNode.ts";
import LinkHandler from "./LinkHandler.tsx";
import ListDialog from "./ListDialog.tsx";
import { Database, Settings2, LayoutList } from "lucide-react";

interface ListItemSummary {
    id: string;
    title: string;
    description: string;
}

interface ListSectionSummary {
    id: string;
    sectionTitle: string;
    items: ListItemSummary[];
}

const List: React.FC<any> = (data) => {
    const { nodes } = useFlowStore();
    const [showDialog, setShowDialog] = useState(false);

    const [mode, setMode] = useState<"dynamic" | "static">("static");
    const [header, setHeader] = useState("");
    const [body, setBody] = useState("");
    const [arrayAttribute, setArrayAttribute] = useState("");
    const [sections, setSections] = useState<ListSectionSummary[]>([]);

    useEffect(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        if (!node) return;
        const content = (node.data as CanvasNodeData).content.find((c) => c.id === data.subNodeId);
        if (!content?.data) return;

        const d = content.data as any;
        setMode(d.mode || (d.arrayAttribute ? "dynamic" : "static"));
        setHeader(d.header || "");
        setBody(d.body || "");
        setArrayAttribute(d.arrayAttribute || "");
        setSections(d.sections || []);
    }, []);

    const onClose = () => setShowDialog(false);

    const totalItems = sections.reduce((sum, sec) => sum + (sec.items?.length ?? 0), 0);

    const allItems = sections.flatMap((sec) =>
        (sec.items || []).map((item) => ({
            ...item,
            sectionTitle: sec.sectionTitle,
        }))
    );

    // No body-edge pruning here: a List's body connector (rendered at the node header, see
    // NodeHeader.tsx) is allowed to point at any node, not just a Smart Delay, so a wire that
    // survives an item reconfiguration is intentional rather than stale.

    const itemConnectionsSection = () => (
        <div style={{ marginTop: "2px" }}>
            <div style={{ fontSize: "9px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                Item Connections
            </div>
            {allItems.map((item) => (
                <div
                    key={item.id}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 0",
                        borderBottom: "1px solid #f1f5f9",
                        position: "relative",
                    }}
                >
                    <span style={{ fontSize: "10px", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "85%" }}>
                        {item.sectionTitle ? `${item.sectionTitle} · ` : ""}
                        {item.title || "Untitled item"}
                    </span>
                    <LinkHandler idx={item.id} />
                </div>
            ))}
        </div>
    );

    return (
        <div style={{ padding: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
                style={{
                    background: "#f8fafc",
                    borderRadius: "10px",
                    padding: "10px 12px",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                }}
            >
                <div
                    style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        background: mode === "static" ? "#ede9fe" : "#dbeafe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {mode === "static" ? <Settings2 size={16} color="#6d28d9" /> : <Database size={16} color="#1d4ed8" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#0f172a", marginBottom: "2px", display: "flex", alignItems: "center", gap: "6px" }}>
                        {mode === "static" ? (
                            <>
                                <LayoutList size={11} color="#6d28d9" />
                                {totalItems} item{totalItems !== 1 ? "s" : ""} (static)
                            </>
                        ) : (
                            <>
                                <Database size={11} color="#1d4ed8" />
                                {arrayAttribute || "No attribute set"}
                            </>
                        )}
                    </div>
                    {header || body ? (
                        <div style={{ fontSize: "10px", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {header ? `${header} — ` : ""}
                            {body || ""}
                        </div>
                    ) : (
                        <div style={{ fontSize: "10px", color: "#f59e0b" }}>List not configured</div>
                    )}
                </div>
            </div>

            <button
                onClick={() => setShowDialog(true)}
                style={{
                    width: "100%",
                    padding: "7px 0",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderRadius: "8px",
                    border: "1px solid var(--primary-bg-color)",
                    background: "white",
                    color: "var(--primary-bg-color)",
                    cursor: "pointer",
                }}
            >
                Configure List
            </button>

            {mode === "static" && allItems.length > 0 && itemConnectionsSection()}

            <Dialog
                header="Configure List"
                visible={showDialog}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.5)", zIndex: 9998 }}
                contentStyle={{ zIndex: 9999, padding: "16px 20px" }}
                style={{ width: "580px", maxWidth: "95vw" }}
                onHide={onClose}
            >
                <ListDialog data={data} onClose={onClose} />
            </Dialog>
        </div>
    );
};

export default List;
